import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import { KEYS, KEYS_DIRECTION } from '../../../constants';

const $header = _$('.header');
let fetchController = new AbortController();

function useHeaderSearch(props) {
  const labels = useMemo(() => JSON.parse(props.labels), []);
  const [focused, setFocused] = useState(false);
  const [term, setTerm] = useState('');
  const [fixedTerm, setFixedTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const { current: $inMenuPrimaryNav } = useRef(_$('.header__panel'));
  const { current: inMenuPrimaryNavFirstPanelId } = useRef(
    _$('.in-menu-primary-nav__trigger').id
  );
  const showResults = suggestions.length && focused && term.length > 2;

  const updateDesignElement = () => {
    const currentDesignElementClass =
      'header-panel__design-element-wrapper--current';
    const current = document.querySelector('[data-nav-id="search"]');
    _$$('.header-panel__design-element-wrapper').forEach(el =>
      el.classList.remove(currentDesignElementClass)
    );
    current?.classList?.add?.(currentDesignElementClass);
  };

  const onChange = e => setTerm(e.target.value);

  const onFocus = () => {
    setFocused(true);
    updateDesignElement();
  };

  const onBlur = () => {
    setFocused(false);
  };

  const onKeyDown = e => {
    switch (e.keyCode) {
    case KEYS.ESC:
      e.preventDefault();
      setTerm('');
      break;
    case KEYS.END:
      e.preventDefault();
      setCurrentResultIndex(suggestions.length - 1);
      break;
    case KEYS.HOME:
      e.preventDefault();
      setCurrentResultIndex(0);
      break;
    case KEYS.UP:
    case KEYS.DOWN:
      e.preventDefault();
      break;
    }
  };

  const onKeyUp = e => {
    switch (e.keyCode) {
    case KEYS.UP:
    case KEYS.DOWN:
      updateCurrentResultIndexOnArrowPress(e);
      break;
    }
  };

  const handleViewAll = () => {
    window.location.href = `${props.searchUrl}?${props.searchUrlQuery}=${fixedTerm}`;
  };

  const handleFocusChange = () => {
    if (focused) {
      $header.classList.add('header--search-focused');
      $header.classList.add('header--base-search-activated');
      $header.dispatchEvent(new CustomEvent('pauseheaderpanelfocustrap'));
    } else {
      $header.classList.remove('header--search-focused');
      $header.dispatchEvent(new CustomEvent('continueheaderpanelfocustrap'));
    }
  };

  const handlePanelClose = () => {
    $header._on('close', reset);
  };

  const hanleShowResultsChange = () => {
    $header.classList.toggle('header--search-suggestions-shown', showResults);
  };

  const handleTermChange = async term => {
    fetchController.abort();
    fetchController = new AbortController();

    if (!term || term.length <= 2) {
      setSuggestions([]);
      return;
    }

    let res = await fetch(`${props.endpoint}?${props.searchUrlQuery}=${term}`, {
      signal: fetchController.signal
    });
    res = await res.json();

    setSuggestions(res?.Results || []);
    setFixedTerm(res?.Keyword || '');
    setCurrentResultIndex(-1);
  };

  const updateCurrentResultIndexOnArrowPress = e => {
    if (!KEYS_DIRECTION[e.keyCode]) return;

    const newIndex = currentResultIndex + KEYS_DIRECTION[e.keyCode];

    if (newIndex < suggestions.length && newIndex >= 0) {
      setCurrentResultIndex(currentResultIndex + KEYS_DIRECTION[e.keyCode]);
      return;
    }

    if (e.keyCode === KEYS.UP) {
      setCurrentResultIndex(suggestions.length - 1);
    } else if (e.keyCode === KEYS.DOWN) {
      setCurrentResultIndex(0);
    }
  };

  const handleSearchSubmit = e => {
    if (!term.length) {
      e.preventDefault();
      return;
    }

    const current = suggestions[currentResultIndex];

    if (current) {
      e.preventDefault();
      window.location = current.Url;
    }

    if (fixedTerm) {
      e.preventDefault();
      window.location = `${props.searchUrl}?${props.searchUrlQuery}=${fixedTerm}`;
    }
  };

  const handleFormReset = e => {
    e.preventDefault();
    setTerm('');
  };

  const reset = () => {
    setFocused(false);
    setTerm('');
    $header.classList.remove('header--base-search-activated');
  };

  const handleClose = () => {
    reset();
    const event = new CustomEvent('activatetab', {
      detail: { id: inMenuPrimaryNavFirstPanelId }
    });
    $inMenuPrimaryNav.dispatchEvent(event);
  };

  const debouncedHandleTermChange = useCallback(
    debounce(handleTermChange, 250),
    []
  );

  useEffect(hanleShowResultsChange, [showResults]);
  useEffect(handleFocusChange, [focused]);
  useEffect(() => debouncedHandleTermChange(term), [term]);
  useEffect(handlePanelClose, []);

  return {
    labels,
    term,
    focused,
    suggestions,
    showResults,
    currentResultIndex,
    handleViewAll,
    handleSearchSubmit,
    handleFormReset,
    handleClose,
    onKeyDown,
    onKeyUp,
    onChange,
    onFocus,
    onBlur
  };
}

export default useHeaderSearch;
